import React, { useState } from 'react';
import { CalendarIcon, ClockIcon, VideoCameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import CustomDropdown from './CustomDropdown';
import { useAuth } from '../contexts/AuthContext';

// Custom styles for DatePicker to match dark theme
const datePickerStyles = `
  .react-datepicker {
    background-color: #2b2d31 !important;
    border: 1px solid #1e1f22 !important;
    color: #e5e7eb !important;
    font-family: inherit !important;
    z-index: 9999 !important;
  }
  
  .datepicker-popper {
    z-index: 9999 !important;
  }
  
  .react-datepicker-popper {
    z-index: 9999 !important;
  }
  
  .react-datepicker__header {
    background-color: #313338 !important;
    border-bottom: 1px solid #1e1f22 !important;
  }
  
  .react-datepicker__current-month,
  .react-datepicker__day-name,
  .react-datepicker__day {
    color: #e5e7eb !important;
  }
  
  .react-datepicker__day:hover {
    background-color: #5865f2 !important;
    color: white !important;
  }
  
  .react-datepicker__day--selected {
    background-color: #5865f2 !important;
    color: white !important;
  }
  
  .react-datepicker__day--keyboard-selected {
    background-color: #5865f2 !important;
    color: white !important;
  }
  
  .react-datepicker__time-container {
    background-color: #313338 !important;
    border-left: 1px solid #1e1f22 !important;
  }
  
  .react-datepicker__time-list-item {
    color: #e5e7eb !important;
  }
  
  .react-datepicker__time-list-item:hover {
    background-color: #5865f2 !important;
    color: white !important;
  }
  
  .react-datepicker__time-list-item--selected {
    background-color: #5865f2 !important;
    color: white !important;
  }
  
  .react-datepicker__navigation {
    border-color: #e5e7eb !important;
  }
  
  .react-datepicker__navigation:hover {
    background-color: #313338 !important;
  }
  
  .react-datepicker__triangle {
    border-bottom-color: #2b2d31 !important;
  }
  
  .react-datepicker__triangle::before {
    border-bottom-color: #1e1f22 !important;
  }
`;

interface CreateZoomMeetingProps {
  onCreateMeeting: (meetingData: any) => Promise<any>;
  onCancel: () => void;
  isOpen: boolean;
  classroom: any; // Add classroom prop to check permissions
}

export const CreateZoomMeeting: React.FC<CreateZoomMeetingProps> = ({
  onCreateMeeting,
  onCancel,
  isOpen,
  classroom
}) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    topic: '',
    startDateTime: new Date(),
    duration: 60
  });
  const [loading, setLoading] = useState(false);

  // Check if current user is the classroom creator or admin
  const isCreator = currentUser && classroom?.createdBy === currentUser.uid;
  const isAdmin = currentUser && classroom?.admins?.includes(currentUser.uid);
  const isSystemAdmin = currentUser && currentUser.role === 'admin';
  
  // Allow creators, classroom admins, and system admins to create meetings
  const canCreateMeeting = isCreator || isAdmin || isSystemAdmin;

  // If user cannot create meetings, don't show the modal
  if (!canCreateMeeting) {
    return null;
  }

  const durationOptions = [
    { value: '30', label: '30 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' },
    { value: '180', label: '3 hours' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.topic || !formData.startDateTime) return;

    setLoading(true);
    try {
      await onCreateMeeting({
        ...formData,
        startTime: formData.startDateTime.toISOString()
      });
      // Reset form
      setFormData({ topic: '', startDateTime: new Date(), duration: 60 });
      onCancel();
    } catch (error) {
      console.error('Failed to create meeting:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{datePickerStyles}</style>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-[#2b2d31] rounded-lg p-6 w-full max-w-md mx-4 border border-[#1e1f22]">
        <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-200">Create Zoom Meeting</h3>
          <button
            onClick={onCancel}
              className="text-gray-400 hover:text-gray-300 p-1 rounded-lg hover:bg-[#313338] transition-colors"
          >
              <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
              Meeting Topic
            </label>
            <div className="relative">
              <VideoCameraIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                placeholder="Enter meeting topic"
                  className="w-full pl-10 pr-3 py-2 border border-[#1e1f22] rounded-md focus:outline-none focus:ring-2 focus:ring-[#5865f2] focus:border-[#5865f2] bg-[#1e1f22] text-gray-200 placeholder-gray-400"
                required
              />
            </div>
          </div>

          <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Start Date & Time
            </label>
            <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
                <DatePicker
                  selected={formData.startDateTime}
                  onChange={(date: Date | null) => {
                    if (date) {
                      setFormData({ ...formData, startDateTime: date });
                    }
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={30}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  minDate={new Date()}
                  minTime={new Date()}
                  maxTime={new Date(new Date().setHours(23, 59, 0, 0))}
                  placeholderText="Select date and time"
                  className="w-full pl-10 pr-3 py-2 border border-[#1e1f22] rounded-md focus:outline-none focus:ring-2 focus:ring-[#5865f2] focus:border-[#5865f2] bg-[#1e1f22] text-gray-200"
                  popperClassName="datepicker-popper"
                  popperPlacement="bottom-start"
                  customInput={
              <input
                      className="w-full pl-10 pr-3 py-2 border border-[#1e1f22] rounded-md focus:outline-none focus:ring-2 focus:ring-[#5865f2] focus:border-[#5865f2] bg-[#1e1f22] text-gray-200 placeholder-gray-400"
                      placeholder="Select date and time"
                    />
                  }
              />
            </div>
          </div>

          <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Duration
            </label>
            <div className="relative">
                <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
                <CustomDropdown
                  value={formData.duration.toString()}
                  onChange={(value) => setFormData({ ...formData, duration: parseInt(value) })}
                  options={durationOptions}
                  placeholder="Select duration"
                  className="pl-10"
                />
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
                className="flex-1 px-4 py-2 border border-[#313338] rounded-md text-gray-300 hover:bg-[#313338] focus:outline-none focus:ring-2 focus:ring-[#5865f2] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
                disabled={loading || !formData.topic || !formData.startDateTime}
                className="flex-1 px-4 py-2 bg-[#5865f2] text-white rounded-md hover:bg-[#4854d4] focus:outline-none focus:ring-2 focus:ring-[#5865f2] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Creating...' : 'Create Meeting'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};
